import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorMessage } from '../api/client';
import * as miscCostsApi from '../api/miscCostsApi';
import {
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorBanner,
  Modal,
  Skeleton,
  Spinner,
  TableSkeleton,
} from '../components/ui';
import { IconTrash } from '../components/icons';
import { MISC_COST_CATEGORIES, MiscCost, MiscCostCategory } from '../types/miscCost';
import { capitalize, formatDate, formatINR, formatINRPrecise } from '../utils/format';

/** YYYY-MM-DD in the admin's own timezone — toISOString() would roll IST
 *  mornings back to the previous day and file expenses under the wrong date. */
function toInputDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const TODAY = toInputDate(new Date());
const MONTH_START = toInputDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1));

// Category chips are colour-coded so a period's shape reads at a glance —
// runaway logistics spend looks different from runaway supplies spend.
const CATEGORY_STYLES: Record<MiscCostCategory, string> = {
  packaging:
    'border-blue-500/30 bg-blue-500/10 text-blue-600 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-300',
  supplies:
    'border-violet-500/30 bg-violet-500/10 text-violet-600 dark:border-violet-400/30 dark:bg-violet-400/10 dark:text-violet-300',
  logistics:
    'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-300',
  other:
    'border-edge bg-edge/40 text-sub',
};

function CategoryChip({ category }: { category: MiscCostCategory | null }) {
  const key = category ?? 'other';
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${CATEGORY_STYLES[key]}`}
    >
      {capitalize(key)}
    </span>
  );
}

const BLANK_EXPENSE = {
  expense_date: TODAY,
  description: '',
  category: 'packaging' as MiscCostCategory,
  amount: '',
  receipt_ref: '',
};

export default function MiscCostsPage() {
  const [costs, setCosts] = useState<MiscCost[]>([]);
  const [summary, setSummary] = useState<{ total: number; count: number; by_category: Record<string, number> }>({
    total: 0,
    count: 0,
    by_category: {},
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');

  // Filters — defaults to the current month, which is the window this page is
  // actually opened for ("what have we spent so far this month?").
  const [from, setFrom] = useState(MONTH_START);
  const [to, setTo] = useState(TODAY);
  const [category, setCategory] = useState<MiscCostCategory | ''>('');

  // Add-expense modal
  const [addOpen, setAddOpen] = useState(false);
  const [newExpense, setNewExpense] = useState(BLANK_EXPENSE);
  const [creating, setCreating] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<MiscCost | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await miscCostsApi.getMiscCosts({ from, to, category });
      setCosts(res.costs);
      setSummary(res.summary);
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [from, to, category]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async () => {
    const description = newExpense.description.trim();
    if (!description) {
      setActionError('Enter a description');
      return;
    }
    const amount = Number(newExpense.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setActionError('Enter an amount greater than 0');
      return;
    }
    if (!newExpense.expense_date) {
      setActionError('Pick the date the cost was incurred');
      return;
    }
    setCreating(true);
    setActionError('');
    try {
      await miscCostsApi.createMiscCost({
        description,
        amount,
        category: newExpense.category,
        expense_date: newExpense.expense_date,
        receipt_ref: newExpense.receipt_ref.trim() || undefined,
      });
      setAddOpen(false);
      setNewExpense({ ...BLANK_EXPENSE, expense_date: TODAY });
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setActionError('');
    try {
      await miscCostsApi.deleteMiscCost(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  // An expense dated outside the filtered window would vanish the moment it is
  // saved, which reads as "it didn't save". Warn instead of silently filing it.
  const outsideRange =
    newExpense.expense_date !== '' && (newExpense.expense_date < from || newExpense.expense_date > to);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Misc Costs</h1>
          <p className="text-sm text-sub">
            Overhead that can't be tied to one order — wrap, tape, supplies, pickup fees
          </p>
        </div>
        <button
          className="btn-gold"
          onClick={() => {
            setNewExpense({ ...BLANK_EXPENSE, expense_date: TODAY });
            setActionError('');
            setAddOpen(true);
          }}
        >
          Add expense
        </button>
      </div>

      {error && <ErrorBanner message={error} onRetry={load} />}
      {actionError && <ErrorBanner message={actionError} />}

      {/* Filters */}
      <Card bodyClassName="!py-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="label" htmlFor="misc-from">
              From
            </label>
            <input
              id="misc-from"
              type="date"
              className="input !w-auto"
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="misc-to">
              To
            </label>
            <input
              id="misc-to"
              type="date"
              className="input !w-auto"
              value={to}
              min={from}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          <div>
            <label className="label" htmlFor="misc-category">
              Category
            </label>
            <select
              id="misc-category"
              className="input !w-auto"
              value={category}
              onChange={(e) => setCategory(e.target.value as MiscCostCategory | '')}
            >
              <option value="">All categories</option>
              {MISC_COST_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {capitalize(c)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Summary — total first, then the same money split by category */}
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <div className="card p-5">
            <p className="text-xs font-medium uppercase tracking-wide text-sub">Total in period</p>
            <p className="mt-2 text-2xl font-semibold">{formatINR(summary.total)}</p>
            <p className="mt-1 text-xs text-sub">
              {summary.count} {summary.count === 1 ? 'expense' : 'expenses'}
            </p>
          </div>
          {MISC_COST_CATEGORIES.map((c) => (
            <div key={c} className="card p-5">
              <p className="text-xs font-medium uppercase tracking-wide text-sub">{capitalize(c)}</p>
              <p className="mt-2 text-2xl font-semibold">{formatINR(summary.by_category[c] ?? 0)}</p>
              <p className="mt-1 text-xs text-sub">
                {summary.total > 0
                  ? `${Math.round(((summary.by_category[c] ?? 0) / summary.total) * 100)}% of total`
                  : '—'}
              </p>
            </div>
          ))}
        </div>
      )}

      <Card bodyClassName="!p-0">
        {loading ? (
          <TableSkeleton rows={5} />
        ) : costs.length === 0 ? (
          <EmptyState
            message="No expenses in this period"
            hint="Widen the date range, or record what was spent on wrap, tape and supplies."
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-edge">
                  <th className="th">Date</th>
                  <th className="th">Description</th>
                  <th className="th">Category</th>
                  <th className="th text-right">Amount</th>
                  <th className="th">Ref</th>
                  <th className="th"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {costs.map((cost) => (
                  <tr key={cost.id} className="transition-colors hover:bg-edge/20">
                    <td className="td whitespace-nowrap text-sub">{formatDate(cost.expense_date)}</td>
                    <td className="td font-medium">{cost.description}</td>
                    <td className="td">
                      <CategoryChip category={cost.category} />
                    </td>
                    <td className="td text-right font-medium">{formatINRPrecise(cost.amount)}</td>
                    <td className="td text-sub">
                      {cost.receipt_ref ? (
                        <span className="block max-w-[200px] truncate" title={cost.receipt_ref}>
                          {cost.receipt_ref}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="td">
                      <button
                        className="btn-ghost !p-1.5 hover:text-bad"
                        onClick={() => setDeleteTarget(cost)}
                        aria-label={`Delete ${cost.description}`}
                        title="Delete expense"
                      >
                        <IconTrash className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Add-expense modal */}
      <Modal
        open={addOpen}
        title="Add expense"
        onClose={() => setAddOpen(false)}
        footer={
          <>
            <button className="btn-outline" onClick={() => setAddOpen(false)} disabled={creating}>
              Cancel
            </button>
            <button
              className="btn-gold"
              onClick={handleCreate}
              disabled={creating || !newExpense.description.trim() || !newExpense.amount}
            >
              {creating && <Spinner />}
              Save expense
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="label">Date</label>
            <input
              className="input"
              type="date"
              value={newExpense.expense_date}
              onChange={(e) => setNewExpense({ ...newExpense, expense_date: e.target.value })}
            />
            <p className="mt-1.5 text-xs text-sub">
              When the cost was incurred, not when it's being entered.
            </p>
          </div>
          <div>
            <label className="label">Description</label>
            <input
              className="input"
              placeholder="e.g. Bubble wrap roll"
              value={newExpense.description}
              onChange={(e) => setNewExpense({ ...newExpense, description: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Category</label>
              <select
                className="input"
                value={newExpense.category}
                onChange={(e) =>
                  setNewExpense({ ...newExpense, category: e.target.value as MiscCostCategory })
                }
              >
                {MISC_COST_CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {capitalize(c)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Amount (₹)</label>
              <input
                className="input"
                type="number"
                min={0}
                step="0.01"
                placeholder="e.g. 450.00"
                value={newExpense.amount}
                onChange={(e) => setNewExpense({ ...newExpense, amount: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="label">Receipt ref (optional)</label>
            <input
              className="input"
              placeholder="Invoice no., vendor, note…"
              value={newExpense.receipt_ref}
              onChange={(e) => setNewExpense({ ...newExpense, receipt_ref: e.target.value })}
            />
          </div>
          {outsideRange && (
            <p className="text-xs text-warn">
              This date falls outside the current filter ({formatDate(from)} – {formatDate(to)}), so
              the expense won't appear in the table until you widen the range.
            </p>
          )}
          <p className="text-xs text-sub">
            Misc costs are period overhead — they are not added to any order's landed cost.
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete expense"
        danger
        confirmLabel="Delete"
        busy={deleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
        message={
          deleteTarget && (
            <>
              Permanently remove{' '}
              <span className="font-semibold text-ink">{deleteTarget.description}</span> (
              {formatINRPrecise(deleteTarget.amount)}, {formatDate(deleteTarget.expense_date)})? This
              can't be undone.
            </>
          )
        }
      />
    </div>
  );
}
